import {
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  Switch,
  useToast,
} from '@ais-ui/ui-core';
import * as z from 'zod';
import { useSettings } from '../../SettingsProvider';
import { useSaveSettings } from '@ais-ui/websockets';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@ais-ui/utils';
import React, { useEffect, useState } from 'react';
import { useLocalization } from '@ais-ui/locale-management';
import { useSettingsPage } from '../../SettingsPageProvider';

const labelFormSchema = z.object({
  assignedMode: z.boolean(),
  virtualAtonType: z.boolean(),
  country: z.boolean(),
  latitude: z.boolean(),
  longitude: z.boolean(),
  length: z.boolean(),
  mmsi: z.boolean(),
  offPosition: z.boolean(),
  state: z.boolean(),
  status: z.boolean(),
  virtualAtonTargetType: z.boolean(),
  width: z.boolean(),
});

type LabelFormValues = z.infer<typeof labelFormSchema>;

export const VirtualAtonHoverLabelForm = () => {
  const { messages } = useLocalization();
  const { toast } = useToast();
  const { settings } = useSettings();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const { mutate: mutateSettings } = useSaveSettings();

  const initialValues = {
    assignedMode:
      settings['ribbonPlugin.settings']?.show_virtual_aton_assigned_mode ||
      false,
    virtualAtonType:
      settings['ribbonPlugin.settings']?.show_virtual_aton_type || false,
    country:
      settings['ribbonPlugin.settings']?.show_virtual_aton_country || false,
    latitude:
      settings['ribbonPlugin.settings']?.show_virtual_aton_latitude || false,
    longitude:
      settings['ribbonPlugin.settings']?.show_virtual_aton_longitude || false,
    length:
      settings['ribbonPlugin.settings']?.show_virtual_aton_length || false,
    mmsi: settings['ribbonPlugin.settings']?.show_virtual_aton_mmsi || false,
    offPosition:
      settings['ribbonPlugin.settings']?.show_virtual_aton_off_position ||
      false,
    state: settings['ribbonPlugin.settings']?.show_virtual_aton_state || false,
    status:
      settings['ribbonPlugin.settings']?.show_virtual_aton_status || false,
    virtualAtonTargetType:
      settings['ribbonPlugin.settings']?.show_virtual_aton_target_type || false,
    width: settings['ribbonPlugin.settings']?.show_virtual_aton_width || false,
  };

  const form = useForm<LabelFormValues>({
    resolver: zodResolver(labelFormSchema),
    defaultValues: initialValues,
  });

  const { setUnsavedSettings } = useSettingsPage();

  useEffect(() => {
    const subscription = form.watch((formValues) => {
      const hasChanges =
        JSON.stringify(formValues) !== JSON.stringify(initialValues);
      setUnsavedSettings(hasChanges);
    });

    return () => subscription.unsubscribe();
  }, [form, form.watch, initialValues, setUnsavedSettings]);

  function onSubmit(data: LabelFormValues) {
    setIsSubmitting(true);
    const settingsToSave = {
      ...settings,
      'ribbonPlugin.settings': {
        ...settings['ribbonPlugin.settings'],
        show_virtual_aton_assigned_mode: data.assignedMode,
        show_virtual_aton_type: data.virtualAtonType,
        show_virtual_aton_country: data.country,
        show_virtual_aton_latitude: data.latitude,
        show_virtual_aton_longitude: data.longitude,
        show_virtual_aton_length: data.length,
        show_virtual_aton_mmsi: data.mmsi,
        show_virtual_aton_off_position: data.offPosition,
        show_virtual_aton_state: data.state,
        show_virtual_aton_status: data.status,
        show_virtual_aton_target_type: data.virtualAtonTargetType,
        show_virtual_aton_width: data.width,
      },
    };
    mutateSettings(settingsToSave, {
      onSuccess: () => {
        toast({
          className: 'update-virtual-aton-hover-settings-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.settingsMapHoverSuccessUpdateMessage,
        });
        setIsSubmitting(false);
        setUnsavedSettings(false);
      },
      onError: () => {
        toast({
          className: 'update-virtual-aton-hover-settings-error-toast',
          title: messages.genericToastTitleError,
          description: messages.settingsMapHoverFailedUpdateMessage,
          variant: 'destructive',
        });
        setIsSubmitting(false);
      },
    });
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                key="mmsi"
                control={form.control}
                name="mmsi"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4 space-x-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapVirtualAtonHoverMmsiLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapVirtualAtonHoverMmsiDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-virtualAtonLabel-mmsi"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="country"
                control={form.control}
                name="country"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4 space-x-2'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapVirtualAtonHoverCountryLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapVirtualAtonHoverCountryDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-virtualAtonLabel-country"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="latitude"
                control={form.control}
                name="latitude"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4 space-x-2'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapVirtualAtonHoverLatitudeModeLabel}
                      </FormLabel>
                      <FormDescription>
                        {
                          messages.settingsMapVirtualAtonHoverLatitudeDescription
                        }
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-virtualAtonLabel-latitude"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="longitude"
                control={form.control}
                name="longitude"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4 space-x-2'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapVirtualAtonHoverLongitudeLabel}
                      </FormLabel>
                      <FormDescription>
                        {
                          messages.settingsMapVirtualAtonHoverLongitudeDescription
                        }
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-virtualAtonLabel-longitude"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="assignedMode"
                control={form.control}
                name="assignedMode"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4 space-x-2">
                    <div className="space-y-0.5 space-x-2">
                      <FormLabel className="text-base">
                        {messages.settingsMapVirtualAtonHoverAssignedModeLabel}
                      </FormLabel>
                      <FormDescription>
                        {
                          messages.settingsMapVirtualAtonHoverAssignedModeDescription
                        }
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-virtualAtonLabel-assignedMode"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="virtualAtonType"
                control={form.control}
                name="virtualAtonType"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4 space-x-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {
                          messages.settingsMapVirtualAtonHoverVirtualAtonTypeLabel
                        }
                      </FormLabel>
                      <FormDescription>
                        {
                          messages.settingsMapVirtualAtonHoverVirtualAtonTypeDescription
                        }
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-virtualAtonLabel-virtual_atonType"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="length"
                control={form.control}
                name="length"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4 space-x-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapVirtualAtonHoverLengthLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapVirtualAtonHoverLengthDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-virtualAtonLabel-length"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="offPosition"
                control={form.control}
                name="offPosition"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4 space-x-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {
                          messages.settingsMapVirtualAtonHoverOffPositionModeLabel
                        }
                      </FormLabel>
                      <FormDescription>
                        {
                          messages.settingsMapVirtualAtonHoverOffPositionDescription
                        }
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-virtualAtonLabel-offPosition"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="state"
                control={form.control}
                name="state"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4 space-x-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapVirtualAtonHoverStateLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapVirtualAtonHoverStateDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-virtualAtonLabel-state"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="status"
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4 space-x-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapVirtualAtonHoverStatusLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapVirtualAtonHoverStatusDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-virtualAtonLabel-status"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="width"
                control={form.control}
                name="width"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4 space-x-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapVirtualAtonHoverWidthLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapVirtualAtonHoverWidthDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-virtualAtonLabel-width"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="virtualAtonTargetType"
                control={form.control}
                name="virtualAtonTargetType"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4 space-x-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapVirtualAtonHoverTargetTypeLabel}
                      </FormLabel>
                      <FormDescription>
                        {
                          messages.settingsMapVirtualAtonHoverTargetTypeDescription
                        }
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-virtualAtonLabel-virtual_atonType"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pr-2">
          <Button
            id="settings-virtualAtonLabels-save-button"
            onClick={form.handleSubmit(onSubmit)}
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? messages.genericSubmitting : messages.genericSubmit}
          </Button>
        </div>
      </form>
    </Form>
  );
};
